import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { RootState } from "../../store";
import TextField from "../../components/TextField/TextField";
import Button from "../../components/UI/Buttons/Button/Button";
import NotificationMessage from "../../components/UI/NotificationMessage/NotificationMessage";
import WorkingHoursEditor, {
  HoursDraft,
  buildHoursDraft,
} from "./WorkingHoursEditor";
import {
  createVolunteerProfile,
  getVolunteerProfile,
  setVolunteerAvailability,
  updateVolunteerProfile,
  updateVolunteerWorkingHours,
} from "../../services/volunteerProfileService";
import {
  VolunteerProfileResponse,
  VolunteerWorkingHoursEntry,
} from "../../types/volunteerProfile.types";
import styles from "./VolunteerProfilePage.module.scss";

const GENERIC_ERROR = "Došlo je do greške. Pokušajte ponovo.";

const VolunteerProfilePage = () => {
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const userId = Number(userInfo?.id);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [profile, setProfile] = useState<VolunteerProfileResponse | null>(null);

  // Identity fields (shared by the create and the edit card)
  const [firstName, setFirstName] = useState("");
  const [secondName, setSecondName] = useState("");
  const [biography, setBiography] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSaved, setProfileSaved] = useState(false);

  // Availability
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(
    null
  );

  // Working hours
  const [hoursDraft, setHoursDraft] = useState<HoursDraft>(() =>
    buildHoursDraft([])
  );
  const [savingHours, setSavingHours] = useState(false);
  const [hoursError, setHoursError] = useState<string | null>(null);
  const [hoursSaved, setHoursSaved] = useState(false);

  const applyProfile = useCallback(
    (next: VolunteerProfileResponse | null) => {
      setProfile(next);
      if (next) {
        setFirstName(next.firstName ?? "");
        setSecondName(next.secondName ?? "");
        setBiography(next.biography ?? "");
        setHoursDraft(buildHoursDraft(next.workingHours));
      } else {
        // Create flow: prefill the names from the account data.
        setFirstName(userInfo?.firstName ?? "");
        setSecondName(userInfo?.secondName ?? "");
        setBiography("");
      }
    },
    [userInfo?.firstName, userInfo?.secondName]
  );

  const loadProfile = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      setLoadError(GENERIC_ERROR);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const next = await getVolunteerProfile(userId);
      applyProfile(next);
    } catch (err) {
      setLoadError("Greška pri učitavanju profila. Pokušajte ponovo.");
    } finally {
      setLoading(false);
    }
  }, [userId, applyProfile]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const saveProfile = async () => {
    setSavingProfile(true);
    setProfileError(null);
    setProfileSaved(false);
    try {
      if (profile) {
        // Update overwrites all three text fields unconditionally, so always
        // send them; workingHours stays omitted so the schedule is untouched.
        await updateVolunteerProfile({
          id: profile.id,
          userId,
          firstName,
          secondName,
          biography,
        });
      } else {
        await createVolunteerProfile({
          userId,
          firstName,
          secondName,
          biography,
        });
      }
      const next = await getVolunteerProfile(userId);
      applyProfile(next);
      setProfileSaved(true);
    } catch (err) {
      setProfileError(GENERIC_ERROR);
    } finally {
      setSavingProfile(false);
    }
  };

  const togglePause = async (nextPaused: boolean) => {
    setSavingAvailability(true);
    setAvailabilityError(null);
    try {
      // available=false means paused; mind the inversion.
      await setVolunteerAvailability(userId, !nextPaused);
      const next = await getVolunteerProfile(userId);
      applyProfile(next);
    } catch (err) {
      setAvailabilityError(GENERIC_ERROR);
    } finally {
      setSavingAvailability(false);
    }
  };

  const saveHours = async (entries: VolunteerWorkingHoursEntry[]) => {
    setSavingHours(true);
    setHoursError(null);
    setHoursSaved(false);
    try {
      await updateVolunteerWorkingHours(userId, entries);
      // Availability is computed on read; refetch for fresh available/paused.
      const next = await getVolunteerProfile(userId);
      applyProfile(next);
      setHoursSaved(true);
    } catch (err) {
      setHoursError(GENERIC_ERROR);
    } finally {
      setSavingHours(false);
    }
  };

  if (loading) {
    return <div className={styles.page}>Učitavanje...</div>;
  }

  if (loadError) {
    return (
      <div className={styles.page}>
        <p className={styles.error} role="alert">
          {loadError}
        </p>
        <Button buttonType="blue" onClick={loadProfile}>
          Pokušaj ponovo
        </Button>
      </div>
    );
  }

  const identityFields = (
    <>
      <div className={styles.fieldsRow}>
        <TextField
          value={firstName}
          onChange={(val: string) => setFirstName(val)}
          label="Ime"
          wrapClass={styles.textFieldWrap}
          disabled={savingProfile}
        />
        <TextField
          value={secondName}
          onChange={(val: string) => setSecondName(val)}
          label="Prezime"
          wrapClass={styles.textFieldWrap}
          disabled={savingProfile}
        />
      </div>
      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="volunteer-biography">
          Biografija
        </label>
        <textarea
          id="volunteer-biography"
          className={styles.textarea}
          rows={5}
          value={biography}
          disabled={savingProfile}
          onChange={(e) => setBiography(e.target.value)}
        />
      </div>
      {profileError && (
        <p className={styles.error} role="alert">
          {profileError}
        </p>
      )}
    </>
  );

  if (!profile) {
    return (
      <div className={styles.page}>
        <h1 className={styles.pageTitle}>Moj profil ovlašćenog lica</h1>
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Kreirajte svoj profil</h2>
          <p className={styles.hint}>
            Dostupnost možete podesiti nakon što kreirate profil.
          </p>
          {identityFields}
          <Button buttonType="blue" onClick={saveProfile} disabled={savingProfile}>
            {savingProfile ? "Čuvanje..." : "Kreiraj profil"}
          </Button>
        </section>
      </div>
    );
  }

  const photoUrl = profile.userPhoto
    ? `${process.env.REACT_APP_CDN_PATH}${profile.userPhoto}`
    : "/profile.png";

  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>Moj profil ovlašćenog lica</h1>

      {profileSaved && (
        <NotificationMessage onClick={() => setProfileSaved(false)}>
          Promene uspešno sačuvane
        </NotificationMessage>
      )}

      <section className={styles.card}>
        <h2 className={styles.cardTitle}>Lični podaci</h2>
        <div className={styles.photoRow}>
          <img className={styles.photo} src={photoUrl} alt="Fotografija profila" />
          <p className={styles.hint}>
            Fotografija se menja na <Link to="/profile">stranici naloga</Link>.
          </p>
        </div>
        {identityFields}
        <Button buttonType="blue" onClick={saveProfile} disabled={savingProfile}>
          {savingProfile ? "Čuvanje..." : "Sačuvaj"}
        </Button>
      </section>

      <section className={styles.card}>
        <h2 className={styles.cardTitle}>Dostupnost</h2>
        <p className={styles.statusStrip}>
          <span className={styles.statusValue}>
            {profile.available ? "Trenutno dostupan" : "Trenutno nedostupan"}
          </span>
          {profile.paused && <span className={styles.pausedBadge}>Pauza</span>}
        </p>
        <label className={styles.pauseToggle}>
          <input
            type="checkbox"
            checked={profile.paused}
            disabled={savingAvailability}
            onChange={(e) => togglePause(e.target.checked)}
          />
          Pauza (dok je uključena, niste dostupni bez obzira na podešenu
          dostupnost)
        </label>
        {availabilityError && (
          <p className={styles.error} role="alert">
            {availabilityError}
          </p>
        )}
      </section>

      <WorkingHoursEditor
        draft={hoursDraft}
        onChange={setHoursDraft}
        onSave={saveHours}
        saving={savingHours}
        saveError={hoursError}
        savedNotice={hoursSaved}
      />
    </div>
  );
};

export default VolunteerProfilePage;
